// The document viewer's own chrome around the pdf.js frame (#12481): a
// quiet row of reading aids above the pages — Outline · ‹ N / M › ·
// − Fit width + on the left; Find and a ⋯ menu (first / last page, rotate,
// print, download) on the right — and an outline column beside them. The
// frame renders pages only (its toolbar and sidebar are hidden by
// fp-chrome.css); every control here sends a `command` packet into it, and
// the frame's `state`, `outline` and `find` packets keep the row in step as
// the reader scrolls or zooms. pdf.js's annotation tools are not offered
// (Gerry: "only the ones that matter, no editing ones"): they marked up a
// throwaway copy that nothing here could save.
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Tooltip } from "@mui/material";
import {
  ArrowDownToLine,
  ChevronDown,
  ChevronFirst,
  ChevronLast,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Check,
  List,
  Minus,
  MoreHorizontal,
  Plus,
  Printer,
  RotateCw,
  Search,
  X,
} from "lucide-react";
import { Menu } from "@/components/FpPages/RMS/design";
import type { FindShortcut } from "./findShortcut";

/** Below this frame width the row keeps the page and zoom groups and the
 *  ⋯ menu, and Find stands down: Outline (32) + page group (~170) + zoom group (~150) +
 *  Find open (~300) + ⋯ (32) + gaps is about 700; a document beside an
 *  artifact in the overlay, or a narrow split, is under that. Keyed on the
 *  row's own frame, not the viewport (rules/frontend.md). */
export const READING_AIDS_COMPACT_BELOW_PX = 700;

/** One outline entry as the frame resolved it: its page, when the
 *  destination named one, and its children. */
export interface OutlineEntry {
  title: string;
  dest: unknown;
  page: number | null;
  items: OutlineEntry[];
}

export interface ViewerState {
  page: number;
  pages: number;
  scale: number;
  /** pdf.js's named scale ("auto", "page-width", "page-fit") or the number
   *  as a string once the reader has zoomed by hand. */
  scaleValue: string;
}

export interface FindState {
  current: number;
  total: number;
  /** pdf.js's FindState: 0 found, 1 not found, 2 wrapped, 3 pending. */
  state: number | null;
}

export type ViewerCommand =
  | { type: "goToPage"; page: number }
  | { type: "zoomIn" }
  | { type: "zoomOut" }
  | { type: "zoomReset" }
  | { type: "setScale"; value: "page-width" | "page-fit" | number }
  | { type: "find"; query: string }
  | { type: "findAgain"; query: string; previous: boolean }
  | { type: "findClose" }
  | { type: "goToOutline"; dest: unknown }
  | { type: "rotate" }
  | { type: "print" };

interface ReadingAidsProps {
  state: ViewerState | null;
  find: FindState | null;
  hasOutline: boolean;
  outlineOpen: boolean;
  onToggleOutline: () => void;
  send: (command: ViewerCommand) => void;
  /** A narrow frame: the page and zoom groups stay, Find stands down. */
  compact: boolean;
  /** The host pane's Download (the stored file), offered in the ⋯ menu.
   *  Absent when the host has none; the frame's own bytes are only the
   *  preview, so the row never saves those. */
  onDownload?: () => void;
}

// No boxes (Gerry, on the bordered groups: "really dont like this design"):
// the row is bare controls in the chrome ink, the grammar of the pane header
// beside it, with hairlines between the groups.
const quietButtonClass =
  "flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-[7px] text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent";
const toggleClass = (on: boolean): string =>
  `flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-[7px] ${
    on
      ? "bg-[var(--fp-ink-wash)] text-[var(--fp-ink)]"
      : "text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)]"
  }`;

/** What the zoom readout says: the named fit while pdf.js is fitting, the
 *  percentage once the reader has zoomed by hand. */
export const zoomLabel = (state: ViewerState | null): string => {
  if (state === null) return "–";
  if (state.scaleValue === "auto" || state.scaleValue === "page-width") {
    return "Fit width";
  }
  if (state.scaleValue === "page-fit") return "Fit page";
  return `${Math.round(state.scale * 100)}%`;
};

const ZOOM_CHOICES: {
  label: string;
  value: "page-width" | "page-fit" | number;
}[] = [
  { label: "Fit width", value: "page-width" },
  { label: "Fit page", value: "page-fit" },
  { label: "100%", value: 1 },
  { label: "150%", value: 1.5 },
  { label: "200%", value: 2 },
];

const zoomChoiceIsCurrent = (
  choice: (typeof ZOOM_CHOICES)[number],
  state: ViewerState | null
): boolean => {
  if (state === null) return false;
  if (choice.value === "page-width") {
    return state.scaleValue === "auto" || state.scaleValue === "page-width";
  }
  if (choice.value === "page-fit") return state.scaleValue === "page-fit";
  return Math.round(state.scale * 100) === choice.value * 100;
};

/** What PdfReader drives from the keyboard. */
export interface ReadingAidsHandle {
  /** Acts on a find key; false when the row has no Find to act with. */
  findShortcut: (shortcut: FindShortcut) => boolean;
}

const ReadingAidsRow: React.ForwardRefRenderFunction<
  ReadingAidsHandle,
  ReadingAidsProps
> = (
  {
    state,
    find,
    hasOutline,
    outlineOpen,
    onToggleOutline,
    send,
    compact,
    onDownload,
  },
  ref
) => {
  const [findOpen, setFindOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pageDraft, setPageDraft] = useState<string | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [zoomAnchor, setZoomAnchor] = useState<HTMLElement | null>(null);
  const findInputRef = useRef<HTMLInputElement>(null);

  // The field takes the cursor when Find opens, so the reader can type at
  // once; closing clears the frame's highlights.
  useEffect(() => {
    if (findOpen) findInputRef.current?.focus();
  }, [findOpen]);
  // Ctrl/Cmd-F opens Find, or returns to it with the query selected to be
  // typed over; Ctrl/Cmd-G steps through the matches once there is a query.
  useImperativeHandle(
    ref,
    () => ({
      findShortcut: (shortcut) => {
        if (compact || state === null) return false;
        if (shortcut !== "open" && query !== "") {
          send({
            type: "findAgain",
            query,
            previous: shortcut === "previous",
          });
          return true;
        }
        setFindOpen(true);
        findInputRef.current?.focus();
        findInputRef.current?.select();
        return true;
      },
    }),
    [compact, state, query, send]
  );
  const closeFind = () => {
    setFindOpen(false);
    setQuery("");
    send({ type: "findClose" });
  };
  // A frame that narrows with Find open folds it away. `send` is a fresh
  // closure each render, so the effect re-runs often; it acts only once.
  useEffect(() => {
    if (compact && findOpen) {
      setFindOpen(false);
      setQuery("");
      send({ type: "findClose" });
    }
  }, [compact, findOpen, send]);

  // Until the frame has reported a document there is nothing to drive.
  const ready = state !== null;
  const pages = state?.pages ?? 0;
  const page = state?.page ?? 0;

  const commitPage = () => {
    if (pageDraft === null) return;
    const wanted = Number.parseInt(pageDraft, 10);
    setPageDraft(null);
    if (Number.isFinite(wanted) && wanted >= 1 && wanted <= pages) {
      send({ type: "goToPage", page: wanted });
    }
  };

  const findSummary =
    find === null || query === ""
      ? null
      : find.total === 0
        ? find.state === 3
          ? "…"
          : "No matches"
        : `${find.current} of ${find.total}`;
  const hasMatches = query !== "" && find !== null && find.total > 0;

  const moreItems = [
    {
      label: "First page",
      icon: ChevronFirst,
      disabled: page <= 1,
      onSelect: () => send({ type: "goToPage", page: 1 }),
    },
    {
      label: "Last page",
      icon: ChevronLast,
      disabled: page >= pages,
      onSelect: () => send({ type: "goToPage", page: pages }),
    },
    {
      label: "Rotate",
      icon: RotateCw,
      disabled: !ready,
      onSelect: () => send({ type: "rotate" }),
    },
    {
      label: "Print",
      icon: Printer,
      disabled: !ready,
      dividerBefore: true,
      onSelect: () => send({ type: "print" }),
    },
    // The host's Download, the same one as its header's: the stored file,
    // never the preview this frame holds (a .pptx's LibreOffice render).
    // Not gated on the pages loading, since it does not read them.
    ...(onDownload
      ? [{ label: "Download", icon: ArrowDownToLine, onSelect: onDownload }]
      : []),
  ];

  return (
    <div
      role="toolbar"
      aria-label="Reading aids"
      // The document viewer pins its notices below this row, never over its
      // controls (#13681, FileViewer/viewerTopChrome.ts).
      data-viewer-toolbar=""
      className={`flex h-10 flex-none items-center border-b border-[var(--fp-border)] bg-[var(--fp-surface)] ${
        compact ? "gap-1 px-1.5" : "gap-1.5 px-2.5"
      }`}
    >
      {/* Only for a document with headings to list (Gerry: "if there is
          no outline, why even show that icon"). */}
      {hasOutline && (
        <>
          <Tooltip title="Outline" arrow placement="bottom" describeChild>
            <span className="flex">
              <button
                type="button"
                aria-label="Outline"
                aria-pressed={outlineOpen}
                onClick={onToggleOutline}
                className={toggleClass(outlineOpen)}
              >
                <List className="h-4 w-4" />
              </button>
            </span>
          </Tooltip>
          <span className="h-4 w-px flex-none bg-[var(--fp-border-strong)]" />
        </>
      )}

      {/* One page has nothing to step through: the arrows show only when
          there is a second page (Gerry: two faded arrows around "1 / 1"). */}
      <span className="flex items-center">
        {pages > 1 && (
          <button
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => send({ type: "goToPage", page: page - 1 })}
            className={quietButtonClass}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        <span className="flex items-center gap-0.5 px-1.5 text-[12.5px] text-[var(--fp-ink-3)]">
          <input
            aria-label="Page"
            inputMode="numeric"
            disabled={!ready}
            value={pageDraft ?? (page > 0 ? String(page) : "")}
            onChange={(event) => setPageDraft(event.target.value)}
            onFocus={(event) => event.target.select()}
            onBlur={commitPage}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                commitPage();
                (event.target as HTMLInputElement).blur();
              }
            }}
            style={{
              width: `calc(${Math.max(1, String(pages).length)}ch + 6px)`,
            }}
            className="h-6 rounded-[5px] border border-transparent bg-transparent text-center tabular-nums text-[var(--fp-ink)] outline-none hover:border-[var(--fp-border-strong)] focus:border-[var(--fp-ink)]"
          />
          <span aria-hidden="true">/</span>
          <span className="sr-only">of</span>
          <span className="tabular-nums">{pages > 0 ? pages : "–"}</span>
        </span>
        {pages > 1 && (
          <button
            type="button"
            aria-label="Next page"
            disabled={page >= pages}
            onClick={() => send({ type: "goToPage", page: page + 1 })}
            className={quietButtonClass}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </span>

      <span className="h-4 w-px flex-none bg-[var(--fp-border-strong)]" />

      <span className="flex items-center">
        <button
          type="button"
          aria-label="Zoom out"
          disabled={!ready}
          onClick={() => send({ type: "zoomOut" })}
          className={quietButtonClass}
        >
          <Minus className="h-4 w-4" />
        </button>
        {/* The readout opens the zoom choices (Gerry: "Is fit width the only
            option"): the two fits and three sizes, the current one marked. */}
        <Tooltip title="Zoom" arrow placement="bottom" describeChild>
          <span className="flex">
            <button
              type="button"
              aria-label="Zoom"
              aria-haspopup="menu"
              aria-expanded={zoomAnchor !== null}
              disabled={!ready}
              onClick={(event) => setZoomAnchor(event.currentTarget)}
              className="flex h-8 cursor-pointer items-center justify-center whitespace-nowrap rounded-[7px] px-1.5 text-[12.5px] tabular-nums text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              {zoomLabel(state)}
            </button>
          </span>
        </Tooltip>
        <Menu
          anchorEl={zoomAnchor}
          open={zoomAnchor !== null}
          onClose={() => setZoomAnchor(null)}
          label="Zoom"
          items={ZOOM_CHOICES.map((choice) => ({
            label: choice.label,
            icon: zoomChoiceIsCurrent(choice, state) ? Check : undefined,
            onSelect: () => send({ type: "setScale", value: choice.value }),
          }))}
        />
        <button
          type="button"
          aria-label="Zoom in"
          disabled={!ready}
          onClick={() => send({ type: "zoomIn" })}
          className={quietButtonClass}
        >
          <Plus className="h-4 w-4" />
        </button>
      </span>

      <span className="min-w-0 flex-1" />

      {!compact &&
        (findOpen ? (
          <span className="flex items-center gap-0.5">
            <input
              ref={findInputRef}
              aria-label="Find in document"
              value={query}
              placeholder="Find"
              onChange={(event) => {
                const next = event.target.value;
                setQuery(next);
                if (next === "") send({ type: "findClose" });
                else send({ type: "find", query: next });
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && query !== "") {
                  send({
                    type: "findAgain",
                    query,
                    previous: event.shiftKey,
                  });
                } else if (event.key === "Escape") {
                  event.stopPropagation();
                  closeFind();
                }
              }}
              className="h-8 w-36 rounded-[8px] border border-[var(--fp-border-strong)] bg-transparent px-2 text-[13px] text-[var(--fp-ink)] outline-none focus:border-[var(--fp-ink)]"
            />
            {/* A slot as wide as "999 of 999" from the moment Find opens,
                so the field holds still as the count comes and changes
                width (Gerry: "the Find bar keeps moving when I type"). */}
            <span
              className="w-[12ch] flex-none whitespace-nowrap px-1 font-mono text-[11px] text-[var(--fp-ink-3)]"
              aria-live="polite"
            >
              {findSummary}
            </span>
            <button
              type="button"
              aria-label="Previous match"
              disabled={!hasMatches}
              onClick={() => send({ type: "findAgain", query, previous: true })}
              className={quietButtonClass}
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Next match"
              disabled={!hasMatches}
              onClick={() =>
                send({ type: "findAgain", query, previous: false })
              }
              className={quietButtonClass}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Close find"
              onClick={closeFind}
              className={quietButtonClass}
            >
              <X className="h-4 w-4" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            aria-label="Find"
            disabled={!ready}
            onClick={() => setFindOpen(true)}
            className="flex h-8 flex-none cursor-pointer items-center gap-1.5 rounded-[7px] px-2 text-[13px] text-[var(--fp-ink-3)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <Search className="h-4 w-4" />
            <span>Find</span>
          </button>
        ))}

      <Tooltip title="More" arrow placement="bottom">
        <button
          type="button"
          aria-label="More viewer actions"
          aria-haspopup="menu"
          aria-expanded={moreAnchor !== null}
          onClick={(event) => setMoreAnchor(event.currentTarget)}
          className={toggleClass(moreAnchor !== null)}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </Tooltip>
      <Menu
        anchorEl={moreAnchor}
        open={moreAnchor !== null}
        onClose={() => setMoreAnchor(null)}
        label="Viewer actions"
        items={moreItems}
      />
    </div>
  );
};

/** The row. Rendered by PdfReader above the frame. */
export const ReadingAids = forwardRef(ReadingAidsRow);

interface OutlineColumnProps {
  outline: OutlineEntry[];
  currentPage: number;
  onPick: (entry: OutlineEntry) => void;
}

/** The last entry (in reading order) whose page is at or before the page on
 *  screen: the section the reader is in. */
const currentEntry = (
  outline: OutlineEntry[],
  currentPage: number
): OutlineEntry | null => {
  let best: OutlineEntry | null = null;
  const walk = (items: OutlineEntry[]) => {
    for (const item of items) {
      if (item.page !== null && item.page <= currentPage) best = item;
      walk(item.items);
    }
  };
  walk(outline);
  return best;
};

/** The outline column: the document's own bookmarks, nested, the current
 *  section marked. */
export const OutlineColumn: React.FC<OutlineColumnProps> = ({
  outline,
  currentPage,
  onPick,
}) => {
  const current = currentEntry(outline, currentPage);
  const rows = (items: OutlineEntry[], depth: number): React.ReactNode =>
    items.map((item, index) => (
      <React.Fragment key={`${depth}-${index}-${item.title}`}>
        <li>
          <button
            type="button"
            aria-current={item === current ? "location" : undefined}
            onClick={() => onPick(item)}
            style={{ paddingLeft: 12 + depth * 14 }}
            className={`block w-full truncate rounded-[6px] py-1.5 pr-3 text-left text-[13px] leading-5 ${
              item === current
                ? "bg-[var(--fp-ink-wash)] text-[var(--fp-ink)]"
                : "text-[var(--fp-ink-2)] hover:bg-[var(--fp-hover)] hover:text-[var(--fp-ink)]"
            }`}
          >
            {item.title}
          </button>
        </li>
        {item.items.length > 0 && rows(item.items, depth + 1)}
      </React.Fragment>
    ));
  return (
    <nav
      aria-label="Outline"
      className="fp-scroll-bare h-full w-60 flex-none overflow-y-auto border-r border-[var(--fp-border)] bg-[var(--fp-surface)] px-2 py-3"
    >
      <ul className="m-0 list-none space-y-0.5 p-0">{rows(outline, 0)}</ul>
    </nav>
  );
};
